// File: src/pages/PersonalOKRs.js

import React, { useState, useEffect, useContext, useMemo } from 'react';
import './MyOKRs.css';
import '../styles/okr-m3-tokens.css';
import api from '../api';
import { AuthContext } from '../context/AuthContext';
import { calculateOKRStatus } from '../utils/okrStatusUtils';
import MetricsBar from '../components/okr/MetricsBar';
import StatusFilterChips from '../components/okr/StatusFilterChips';
import ObjectiveCard from '../components/okr/ObjectiveCard';

export default function PersonalOKRs() {
  const { user } = useContext(AuthContext);
  const [okrs, setOkrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    api.get('/okrs')
      .then(({ data }) => {
        // Ensure we have an array
        if (!Array.isArray(data)) {
          console.error('Expected array but got:', typeof data, data);
          setOkrs([]);
          setLoading(false);
          return;
        }
        
        // filter to just the OKRs owned by this user
        const mine = data.filter(o =>
          Array.isArray(o.owners)
            ? o.owners.includes(user.name)
            : o.owner === user.name
        );
        const init = {};
        mine.forEach(o => { init[o._id] = false; });
        setExpandedMap(init);
        setOkrs(mine);
      })
      .catch(err => {
        console.error('Error fetching OKRs:', err);
        setError(err.message || 'Failed to load OKRs');
        setOkrs([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user]);

  const toggleExpand = id =>
    setExpandedMap(m => ({ ...m, [id]: !m[id] }));
  
  const expandAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, true])));
  const collapseAll = () =>
    setExpandedMap(Object.fromEntries(okrs.map(o => [o._id, false])));
  
  const handleToggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  const okrsWithStatus = useMemo(() => {
    const withStatus = okrs.map(okr => ({
      ...okr,
      status: calculateOKRStatus(okr.keyResults || [], okr.deadline)
    }));
    
    if (selectedStatuses.length === 0) return withStatus;
    return withStatus.filter(okr => selectedStatuses.includes(okr.status));
  }, [okrs, selectedStatuses]);
  
  const { overallProgress, overallScore, totalOkrs, totalKRs } = useMemo(() => {
    const totalOkrs = okrs.length;
    let totalKRs = 0, done = 0, sum = 0;
    okrs.forEach(o => {
      totalKRs += o.keyResults.length;
      done += o.keyResults.filter(kr => kr.completed).length;
      sum += o.score || 0;
    });
    const overallProgress = totalKRs ? Math.round((done / totalKRs) * 100) : 0;
    const overallScore = totalOkrs ? sum / totalOkrs : 0;
    return { overallProgress, overallScore, totalOkrs, totalKRs };
  }, [okrs]);

  // toggle a KR completed state and persist
  const handleToggleKR = async (okrId, idx) => {
    const target = okrs.find(o => o._id === okrId);
    if (!target || !target.keyResults || !target.keyResults[idx]) {
      console.error('❌ [PersonalOKRs] Invalid OKR or key result index:', { okrId, idx, target: !!target });
      return;
    }

    // Toggle the completed status
    const currentCompleted = target.keyResults[idx].completed || false;
    const newCompleted = !currentCompleted;
    
    console.log('🔄 [PersonalOKRs] Toggling KR:', { 
      okrId, 
      idx, 
      current: currentCompleted, 
      new: newCompleted,
      krText: target.keyResults[idx].text?.substring(0, 30)
    });

    // Create new keyResults array with updated completed status
    const newKRs = target.keyResults.map((kr, i) => {
      if (i === idx) {
        return {
          text: kr.text || '',
          team: kr.team || undefined,
          krOwner: kr.krOwner || undefined,
          completed: newCompleted, // Explicit boolean
          deadline: kr.deadline || undefined
        };
      }
      return {
        text: kr.text || '',
        team: kr.team || undefined,
        krOwner: kr.krOwner || undefined,
        completed: kr.completed === true || kr.completed === 'true' || false, // Ensure boolean
        deadline: kr.deadline || undefined
      };
    });
    
    const newProgress = newKRs.length
      ? Math.round((newKRs.filter(k => k.completed === true).length / newKRs.length) * 100)
      : 0;
    const newStatus = calculateOKRStatus(newKRs, target.deadline);

    // Prepare payload - only send necessary fields
    const payload = {
      objective: target.objective,
      deadline: target.deadline,
      level: target.level,
      owners: target.owners || [target.owner].filter(Boolean),
      owner: target.owner,
      year: target.year,
      quarter: target.quarter,
      type: target.type,
      keyResults: newKRs,
      progress: newProgress,
      score: target.score || 0,
      status: newStatus
    };

    console.log('📤 [PersonalOKRs] Sending payload:', {
      okrId,
      keyResultsCount: payload.keyResults.length,
      completedCount: payload.keyResults.filter(kr => kr.completed).length,
      progress: payload.progress
    });

    // Optimistically update UI
    setOkrs(list =>
      list.map(o =>
        o._id === okrId
          ? { ...target, ...payload }
          : o
      )
    );

    try {
      const response = await api.put(`/okrs/${okrId}`, payload);
      // Update with server response to ensure we have the saved data
      const savedOKR = response.data;
      console.log('✅ [PersonalOKRs] Saved OKR received:', {
        id: savedOKR._id,
        keyResults: savedOKR.keyResults?.map((kr, i) => ({
          idx: i,
          text: kr.text?.substring(0, 20),
          completed: kr.completed,
          type: typeof kr.completed
        }))
      });
      setOkrs(list =>
        list.map(o =>
          o._id === okrId ? savedOKR : o
        )
      );
    } catch (e) {
      // Revert on error
      setOkrs(list =>
        list.map(o =>
          o._id === okrId ? target : o
        )
      );
      console.error('❌ [PersonalOKRs] Failed to update OKR:', e);
      alert('Could not save change. Please try again.');
    }
  };


  if (loading) {
    return <div className="m3-center-message">Loading…</div>;
  }

  if (error) {
    return <div className="m3-center-message m3-error">Error: {error}</div>;
  }

  return (
    <div className="m3-my-okrs-page">
      <div className="m3-page-header">
        <h1 className="m3-page-title">My OKRs</h1>
      </div>

      {/* Metrics Bar */}
      {okrs.length > 0 && (
        <MetricsBar
          progress={overallProgress}
          score={overallScore}
          objectives={totalOkrs}
          keyResults={totalKRs}
        />
      )}

      {/* Status Filter Chips */}
      <StatusFilterChips
        selectedStatuses={selectedStatuses}
        onToggleStatus={handleToggleStatus}
      />

      {/* Expand/Collapse Controls */}
      {okrs.length > 0 && (
        <div className="m3-expand-controls">
          <button className="m3-button m3-button-text" onClick={expandAll}>
            Expand All
          </button>
          <button className="m3-button m3-button-text" onClick={collapseAll}>
            Collapse All
          </button>
        </div>
      )}

      {/* OKRs List */}
      <section className="m3-okrs-content">
        {okrsWithStatus.length === 0 ? (
          <div className="m3-empty-state">
            <p>You have no OKRs yet{selectedStatuses.length > 0 ? ' matching selected filters' : ''}.</p>
          </div>
        ) : (
          <div className="m3-okrs-list">
            {okrsWithStatus.map(okr => (
              <ObjectiveCard
                key={okr._id}
                okr={okr}
                isExpanded={expandedMap[okr._id] || false}
                onToggleExpand={toggleExpand}
                onToggleKR={handleToggleKR}
                canEdit={false}
                canDelete={false}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
